import React, { SyntheticEvent, useCallback, useMemo } from 'react';
import { useController, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Chip, Paper, Stack, Typography } from '@mui/material';
import HomeWorkOutlinedIcon from '@mui/icons-material/HomeWorkOutlined';
import { useMutation } from '@apollo/client';
import { FormattedMessage } from 'react-intl';

import {
  CREATE_SITE_MUTATION,
  GET_MY_SITES_QUERY,
  UPDATE_SITE_MUTATION,
} from '../../../gql';
import { siteTypes } from '../../../consts';
import { useVocabulary } from '../../../i18n';
import {
  Button,
  FieldLabel,
  FormField,
  SurfaceAutocomplete,
  SurfaceTextField,
  Switch,
} from '../../../components/design-system';
import { AmcrProjectIdsField } from './AmcrProjectIdsField';
import { SiteTitlePhoto } from './SiteTitlePhoto';

interface SiteFormProps {
  id?: string;
  site?: {
    name: string;
    latitude?: string | number;
    longitude?: string | number;
    description?: string;
    localName?: string;
    siteType?: string;
    visibility: 'public' | 'private';
    shared?: boolean;
    image?: string | null;
    imageCropPosition?: {
      x: number;
      y: number;
    } | null;
    images?: string[];
    amcrProjectIds?: string[] | null;
  };
}

interface SiteFormValues {
  name: string;
  description: string;
  localName: string;
  siteType: string | null;
  latitude: string | number;
  longitude: string | number;
  public: boolean;
  amcrProjectIds: string[];
}

const formStyles = { width: '100%' };
const titlePhotoWrapperStyles = { position: 'relative' };
const identityStyles = {
  mt: { xs: 1.5, sm: -3.5 },
  ml: { xs: 0, sm: 2.5 },
  position: 'relative',
  zIndex: 1,
  alignItems: { xs: 'flex-start', sm: 'flex-end' },
};
const emblemStyles = {
  width: 88,
  height: 88,
  borderRadius: '50%',
  display: { xs: 'none', sm: 'grid' },
  placeItems: 'center',
  color: '#c8943d',
  bgcolor: 'background.default',
  border: '1px solid',
  borderColor: 'divider',
  boxShadow: 'inset 0 0 0 5px rgba(200, 148, 61, 0.12)',
  flexShrink: 0,
};
const emblemIconStyles = { fontSize: 48 };
const titleDetailsStyles = { pb: { sm: 0.5 } };
const siteTitleStyles = {
  color: 'text.primary',
  fontSize: { xs: '1.5rem', sm: '1.85rem' },
  lineHeight: 1.2,
};
const subtitleStyles = { mt: 0.5, alignItems: 'center', flexWrap: 'wrap' };
const visibilityChipStyles = {
  height: 24,
  borderRadius: 1,
  bgcolor: 'action.selected',
  color: 'text.secondary',
};
const cardsStyles = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', lg: '1.1fr 0.95fr' },
  gap: 2,
};
const cardStyles = {
  p: { xs: 2.5, sm: 3 },
  border: '1px solid',
  borderColor: 'softBorder',
  backgroundImage: 'none',
};
const cardTitleStyles = { mb: 3, fontWeight: 650 };
const fieldsStyles = { gap: 2.5 };
const fieldRowStyles = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', sm: '120px minmax(0, 1fr)' },
  gap: { xs: 0.75, sm: 2 },
  alignItems: 'center',
};
const multilineFieldRowStyles = {
  ...fieldRowStyles,
  alignItems: 'start',
};
const coordinateRowStyles = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
  gap: 2,
};
const coordinateFieldStyles = { gap: 0.75 };
const visibilityRowStyles = { alignItems: 'flex-start', mt: 0.75 };
const visibilityControlStyles = { mt: 1, alignItems: 'center' };
const visibilityHelpStyles = { color: 'text.secondary', mt: 0.25 };
const actionsStyles = {
  borderTop: '1px solid',
  borderColor: 'softBorder',
  pt: 2.5,
  justifyContent: 'flex-end',
};
const actionButtonStyles = { minWidth: 104 };
const savedAlertStyles = { py: 0, alignItems: 'center' };

const toCoordinate = (value: string | number) => {
  if (value === '' || value == null) {
    return undefined;
  }

  return parseFloat(value.toString());
};

const SiteForm = React.memo(({ id, site }: SiteFormProps) => {
  const vocabulary = useVocabulary();
  const navigate = useNavigate();
  const [updateSite, updateState] = useMutation(UPDATE_SITE_MUTATION, {
    refetchQueries: [{ query: GET_MY_SITES_QUERY }],
  });
  const [createSite, createState] = useMutation(CREATE_SITE_MUTATION, {
    onCompleted: ({ createSite: { site: createdSite } }) => {
      if (createdSite) {
        navigate(`/s/${createdSite.id}`);
      }
    },
    refetchQueries: [{ query: GET_MY_SITES_QUERY }],
  });
  const called = updateState.called || createState.called;
  const loading = updateState.loading || createState.loading;
  const isShared = Boolean(site?.shared);
  const isOwner = Boolean(id && site && !isShared);

  const defaultValues = useMemo<SiteFormValues>(
    () => ({
      name: site?.name || '',
      description: site?.description || '',
      localName: site?.localName || '',
      siteType: site?.siteType || null,
      latitude: site?.latitude ?? '',
      longitude: site?.longitude ?? '',
      public: site?.visibility === 'public',
      amcrProjectIds: site?.amcrProjectIds || [],
    }),
    [site]
  );
  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm<SiteFormValues>({ defaultValues });
  const name = useController({ name: 'name', control });
  const description = useController({ name: 'description', control });
  const localName = useController({ name: 'localName', control });
  const siteType = useController({ name: 'siteType', control });
  const latitude = useController({ name: 'latitude', control });
  const longitude = useController({ name: 'longitude', control });
  const visibility = useController({ name: 'public', control });
  const amcrProjectIds = useController({ name: 'amcrProjectIds', control });

  const onSubmit = useCallback(
    async (values: SiteFormValues) => {
      const siteInput = {
        name: values.name,
        description: values.description,
        localName: values.localName,
        siteType: values.siteType,
        latitude: toCoordinate(values.latitude),
        longitude: toCoordinate(values.longitude),
        visibility: values.public ? 'public' : 'private',
        // The server accepts project IDs only from the site owner.
        ...(isOwner ? { amcrProjectIds: values.amcrProjectIds } : {}),
      };

      if (id) {
        await updateSite({ variables: { id, site: siteInput } });
      } else {
        await createSite({ variables: { site: siteInput } });
      }

      reset(values);
    },
    [createSite, id, isOwner, reset, updateSite]
  );
  const handleSiteTypeChange = useCallback(
    (_event: SyntheticEvent, value: string | null) => {
      siteType.field.onChange(value);
    },
    [siteType.field]
  );
  const getSiteTypeLabel = useCallback(
    (option: string) => vocabulary('siteType', option),
    [vocabulary]
  );
  const handleLatitudeChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      latitude.field.onChange(event.target.value.replace(',', '.'));
    },
    [latitude.field]
  );
  const handleLongitudeChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      longitude.field.onChange(event.target.value.replace(',', '.'));
    },
    [longitude.field]
  );
  const handleVisibilityChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      visibility.field.onChange(event.target.checked);
    },
    [visibility.field]
  );
  const siteTypeTextFieldProps = useMemo(
    () => ({ inputRef: siteType.field.ref }),
    [siteType.field.ref]
  );

  return (
    <Stack
      component="form"
      gap={3}
      onSubmit={handleSubmit(onSubmit)}
      sx={formStyles}
    >
      {id && site && (
        <>
          <Box sx={titlePhotoWrapperStyles}>
            <SiteTitlePhoto
              siteId={id}
              siteName={site.name}
              image={site.image}
              imageCropPosition={site.imageCropPosition}
              readOnly={isShared}
            />
          </Box>
          <Stack direction="row" gap={2} sx={identityStyles}>
            <Box sx={emblemStyles}>
              <HomeWorkOutlinedIcon sx={emblemIconStyles} />
            </Box>
            <Box sx={titleDetailsStyles}>
              <Typography component="h1" sx={siteTitleStyles}>
                {site.name}
              </Typography>
              <Stack direction="row" gap={1.25} sx={subtitleStyles}>
                <Typography color="text.secondary">
                  {site.siteType ? vocabulary('siteType', site.siteType) : '—'}
                </Typography>
                <Chip
                  size="small"
                  label={
                    site.visibility === 'public' ? (
                      <FormattedMessage
                        id="site.visibility.public"
                        defaultMessage="Public site"
                      />
                    ) : (
                      <FormattedMessage
                        id="site.visibility.private"
                        defaultMessage="Private site"
                      />
                    )
                  }
                  sx={visibilityChipStyles}
                />
              </Stack>
            </Box>
          </Stack>
        </>
      )}

      <Box sx={cardsStyles}>
        <Paper elevation={0} sx={cardStyles}>
          <Typography variant="h6" sx={cardTitleStyles}>
            <FormattedMessage
              id="site.settings.basicInformation"
              defaultMessage="Basic information"
            />
          </Typography>
          <Stack sx={fieldsStyles}>
            <Box sx={fieldRowStyles}>
              <FieldLabel htmlFor="site-name">
                <FormattedMessage
                  id="site.field.name"
                  defaultMessage="Site name"
                />
              </FieldLabel>
              <SurfaceTextField
                id="site-name"
                name={name.field.name}
                value={name.field.value}
                inputRef={name.field.ref}
                disabled={isShared}
                onBlur={name.field.onBlur}
                onChange={name.field.onChange}
                size="small"
                surface="sunken"
              />
            </Box>
            <Box sx={fieldRowStyles}>
              <FieldLabel htmlFor="site-local-name">
                <FormattedMessage
                  id="site.field.localName"
                  defaultMessage="Local name"
                />
              </FieldLabel>
              <SurfaceTextField
                id="site-local-name"
                name={localName.field.name}
                value={localName.field.value}
                inputRef={localName.field.ref}
                disabled={isShared}
                onBlur={localName.field.onBlur}
                onChange={localName.field.onChange}
                size="small"
                surface="sunken"
              />
            </Box>
            <Box sx={fieldRowStyles}>
              <FieldLabel htmlFor="site-type">
                <FormattedMessage
                  id="site.field.type"
                  defaultMessage="Site type"
                />
              </FieldLabel>
              <SurfaceAutocomplete
                disablePortal
                id="site-type"
                options={siteTypes}
                value={siteType.field.value}
                disabled={isShared}
                getOptionLabel={getSiteTypeLabel}
                onChange={handleSiteTypeChange}
                surface="sunken"
                textFieldProps={siteTypeTextFieldProps}
              />
            </Box>
            <Box sx={multilineFieldRowStyles}>
              <FieldLabel htmlFor="site-description">
                <FormattedMessage
                  id="form.description"
                  defaultMessage="Description"
                />
              </FieldLabel>
              <SurfaceTextField
                id="site-description"
                name={description.field.name}
                value={description.field.value}
                inputRef={description.field.ref}
                disabled={isShared}
                multiline
                minRows={3}
                maxRows={5}
                onBlur={description.field.onBlur}
                onChange={description.field.onChange}
                surface="sunken"
              />
            </Box>
          </Stack>
        </Paper>

        <Paper elevation={0} sx={cardStyles}>
          <Typography variant="h6" sx={cardTitleStyles}>
            <FormattedMessage
              id="site.settings.locationAndVisibility"
              defaultMessage="Location and visibility"
            />
          </Typography>
          <Stack sx={fieldsStyles}>
            <Box sx={coordinateRowStyles}>
              <FormField sx={coordinateFieldStyles}>
                <FieldLabel htmlFor="site-latitude">
                  <FormattedMessage
                    id="site.field.latitude"
                    defaultMessage="Latitude"
                  />
                </FieldLabel>
                <SurfaceTextField
                  id="site-latitude"
                  name={latitude.field.name}
                  value={latitude.field.value}
                  inputRef={latitude.field.ref}
                  disabled={isShared}
                  onBlur={latitude.field.onBlur}
                  onChange={handleLatitudeChange}
                  size="small"
                  surface="sunken"
                />
              </FormField>
              <FormField sx={coordinateFieldStyles}>
                <FieldLabel htmlFor="site-longitude">
                  <FormattedMessage
                    id="site.field.longitude"
                    defaultMessage="Longitude"
                  />
                </FieldLabel>
                <SurfaceTextField
                  id="site-longitude"
                  name={longitude.field.name}
                  value={longitude.field.value}
                  inputRef={longitude.field.ref}
                  disabled={isShared}
                  onBlur={longitude.field.onBlur}
                  onChange={handleLongitudeChange}
                  size="small"
                  surface="sunken"
                />
              </FormField>
            </Box>
            <Box sx={visibilityRowStyles}>
              <FieldLabel htmlFor="site-visibility">
                <FormattedMessage
                  id="site.field.public"
                  defaultMessage="Public"
                />
              </FieldLabel>
              <Stack direction="row" gap={1.25} sx={visibilityControlStyles}>
                <Switch
                  id="site-visibility"
                  name={visibility.field.name}
                  inputRef={visibility.field.ref}
                  disabled={isShared}
                  checked={visibility.field.value}
                  onBlur={visibility.field.onBlur}
                  onChange={handleVisibilityChange}
                />
                <Typography variant="caption" sx={visibilityHelpStyles}>
                  {visibility.field.value ? (
                    <FormattedMessage
                      id="site.visibility.publicHelp"
                      defaultMessage="Anyone can view this site"
                    />
                  ) : (
                    <FormattedMessage
                      id="site.visibility.privateHelp"
                      defaultMessage="The site is available only to collaborators"
                    />
                  )}
                </Typography>
              </Stack>
            </Box>
            {isOwner && (
              <AmcrProjectIdsField
                value={amcrProjectIds.field.value}
                inputRef={amcrProjectIds.field.ref}
                onBlur={amcrProjectIds.field.onBlur}
                onChange={amcrProjectIds.field.onChange}
              />
            )}
          </Stack>
        </Paper>
      </Box>

      <Stack direction="row" gap={1.5} sx={actionsStyles}>
        {called && !isDirty && (
          <Alert severity="success" sx={savedAlertStyles}>
            <FormattedMessage id="form.saved" defaultMessage="Saved!" />
          </Alert>
        )}
        <Button
          disabled={loading || !isDirty || isShared}
          type="submit"
          sx={actionButtonStyles}
        >
          <FormattedMessage id="form.submit" defaultMessage="Submit" />
        </Button>
      </Stack>
    </Stack>
  );
});

export { SiteForm };
